import type { DashboardUsageSummaryItem } from '@protoss/shared';
import {
  Button,
  IconSize,
  ReloadIcon,
  ResizableTable,
  Tooltip,
  type ResizableColumnDef,
} from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { formatDate } from '../../../lib/datetime';
import { useDashboardConsumptionData } from '../hooks/useDashboardConsumptionData';

interface DashboardConsumptionViewProps {
  projectUuid: string;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

type DashboardSortKey =
  | 'assetName'
  | 'assetType'
  | 'totalViewCount'
  | 'totalUserDays'
  | 'latestActivityDate';

const DASHBOARD_COLUMNS: ResizableColumnDef<DashboardUsageSummaryItem>[] = [
  {
    key: 'assetName',
    label: 'Name',
    defaultWidth: 280,
    minWidth: 120,
    maxWidth: 600,
    sortable: true,
    render: (item) => (
      <div className="consumption-table__name">
        <span className="consumption-table__short-name">{item.assetName}</span>
        <span className="consumption-table__full-name">{item.assetId}</span>
      </div>
    ),
  },
  {
    key: 'assetType',
    label: 'Type',
    defaultWidth: 100,
    minWidth: 70,
    maxWidth: 160,
    sortable: true,
    render: (item) => (
      <span className={`consumption-asset-badge consumption-asset-badge--${item.assetType}`}>
        {item.assetType}
      </span>
    ),
  },
  {
    key: 'totalViewCount',
    label: 'Views',
    defaultWidth: 110,
    minWidth: 70,
    maxWidth: 200,
    sortable: true,
    headerClassName: 'rt-table__col--right',
    cellClassName: 'rt-table__col--right',
    render: (item) => formatNumber(item.totalViewCount),
  },
  {
    key: 'totalUserDays',
    label: 'User-days',
    defaultWidth: 130,
    minWidth: 80,
    maxWidth: 200,
    sortable: true,
    headerClassName: 'rt-table__col--right',
    cellClassName: 'rt-table__col--right',
    render: (item) => formatNumber(item.totalUserDays),
  },
  {
    key: 'latestActivityDate',
    label: 'Last active',
    defaultWidth: 140,
    minWidth: 100,
    maxWidth: 220,
    sortable: true,
    headerClassName: 'rt-table__col--right',
    cellClassName: 'rt-table__col--right',
    render: (item) => formatDate(item.latestActivityDate),
  },
];

export function DashboardConsumptionView({ projectUuid }: DashboardConsumptionViewProps) {
  const { settings, summary, loading, syncing, error, syncError, reload, triggerSync } =
    useDashboardConsumptionData(projectUuid);

  const [syncBanner, setSyncBanner] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const [sortKey, setSortKey] = React.useState<DashboardSortKey | null>(null);
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  function handleSort(key: string) {
    const k = key as DashboardSortKey;
    if (k === sortKey) {
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortKey(null);
      }
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
  }

  async function handleSync() {
    setSyncBanner(null);
    try {
      await triggerSync();
      setSyncBanner({ type: 'success', message: 'Sync complete.' });
    } catch {
      // syncError is set on the hook; shown via banner below
    }
  }

  React.useEffect(() => {
    if (syncError) {
      setSyncBanner({ type: 'error', message: syncError });
    }
  }, [syncError]);

  const assets = summary?.assets ?? [];
  const totalViews = assets.reduce((sum, a) => sum + a.totalViewCount, 0);
  const totalUserDays = assets.reduce((sum, a) => sum + a.totalUserDays, 0);

  const sortedAssets = React.useMemo(() => {
    if (!sortKey) return assets;
    return [...assets].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'assetName':
          cmp = a.assetName.localeCompare(b.assetName);
          break;
        case 'assetType':
          cmp = a.assetType.localeCompare(b.assetType);
          break;
        case 'totalViewCount':
          cmp = a.totalViewCount - b.totalViewCount;
          break;
        case 'totalUserDays':
          cmp = a.totalUserDays - b.totalUserDays;
          break;
        case 'latestActivityDate':
          cmp = (a.latestActivityDate ?? '').localeCompare(b.latestActivityDate ?? '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [assets, sortKey, sortDir]);

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="ss-panel ss-panel--fill ss-panel--loading" aria-label="Loading Dashboards">
        Loading…
      </div>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (error && error !== 'POWERBI_NOT_CONFIGURED' && error !== 'POWERBI_WORKSPACE_NOT_SPECIFIED') {
    return (
      <div className="ss-panel ss-panel--fill" aria-label="Dashboards error">
        <PageErrorState message="Failed to load dashboard consumption data." onRetry={reload} />
      </div>
    );
  }

  // ── Not configured ──────────────────────────────────────────────────────────
  const availability = settings?.availability;
  if (!settings || availability === 'not_configured' || error === 'POWERBI_NOT_CONFIGURED') {
    return (
      <div className="consumption-empty">
        <p className="consumption-empty__title">No Power BI integration configured</p>
        <p className="consumption-empty__body">
          Connect a Power BI integration to start tracking report and dashboard usage.
        </p>
      </div>
    );
  }

  // ── Workspace not specified ─────────────────────────────────────────────────
  if (availability === 'workspace_not_specified' || error === 'POWERBI_WORKSPACE_NOT_SPECIFIED') {
    return (
      <div className="consumption-empty">
        <p className="consumption-empty__title">Power BI workspace not configured</p>
        <p className="consumption-empty__body">
          Set a Power BI workspace ID in your project integration settings to track usage.
        </p>
      </div>
    );
  }

  // ── Main view ───────────────────────────────────────────────────────────────
  return (
    <div className="consumption-dashboards-view">
      {/* Sync actions row */}
      <div className="consumption-dashboards-view__actions">
        {settings.lastSyncedAt && (
          <span className="consumption-page__last-sync">
            Last synced {formatDate(settings.lastSyncedAt)}
          </span>
        )}
        <div className="consumption-page__header-actions">
          <Tooltip.Simple label="Sync now">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleSync()}
              disabled={syncing}
              aria-label="Sync dashboard consumption data from Power BI"
            >
              {syncing && (
                <ReloadIcon
                  width={IconSize.md}
                  height={IconSize.md}
                  aria-hidden="true"
                  className="ss-panel__action-icon--spinning"
                />
              )}
              {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
          </Tooltip.Simple>
          <Tooltip.Simple label="Reload">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
              disabled={loading || syncing}
              aria-label="Reload dashboard consumption data"
            >
              Reload
            </Button>
          </Tooltip.Simple>
        </div>
      </div>

      {/* Sync result banner */}
      {syncBanner && (
        <div
          className={`consumption-page__sync-banner consumption-page__sync-banner--${syncBanner.type}`}
          role="status"
          aria-live="polite"
        >
          {syncBanner.message}
        </div>
      )}

      {/* Summary metrics */}
      <div className="consumption-metrics" aria-label="Dashboard usage summary">
        <div className="consumption-metric">
          <span className="consumption-metric__value">{formatNumber(assets.length)}</span>
          <span className="consumption-metric__label">Assets tracked</span>
        </div>
        <div className="consumption-metric">
          <span className="consumption-metric__value">{formatNumber(totalViews)}</span>
          <span className="consumption-metric__label">
            Total views ({summary?.fromDate?.slice(0, 7) ?? '-'})
          </span>
        </div>
        <div className="consumption-metric">
          <span className="consumption-metric__value">{formatNumber(totalUserDays)}</span>
          <span className="consumption-metric__label">User-days</span>
        </div>
        <div className="consumption-metric">
          <span className="consumption-metric__value">{settings.trackedAssetCount}</span>
          <span className="consumption-metric__label">Assets in workspace</span>
        </div>
      </div>

      {/* Asset list */}
      <div className="consumption-table-wrap">
        <div className="consumption-table-header">
          <h2 className="consumption-table-header__title">Reports &amp; dashboards by views</h2>
          <span className="consumption-table-header__count">
            {assets.length} asset{assets.length !== 1 ? 's' : ''}
            {summary && ` · ${formatDate(summary.fromDate)} – ${formatDate(summary.toDate)}`}
          </span>
        </div>

        {sortedAssets.length === 0 ? (
          <div className="consumption-empty">
            <p className="consumption-empty__title">No usage data yet</p>
            <p className="consumption-empty__body">
              Click <strong>Sync now</strong> to pull activity data from Power BI.
            </p>
          </div>
        ) : (
          <div className="consumption-table-scroll">
            <ResizableTable
              columns={DASHBOARD_COLUMNS}
              rows={sortedAssets}
              getRowKey={(a) => `${a.assetType}::${a.assetId}`}
              sortKey={sortKey ?? undefined}
              sortDir={sortDir}
              onSort={handleSort}
              aria-label="Dashboard and report consumption"
            />
          </div>
        )}
      </div>
    </div>
  );
}
